import { challengeSidebarNavText } from '@/src/components/ui/molecules/MyJams/JamChallengeDetails/JamTask/ChallengeSidebarLabel';
import TaskStatus from '@/src/components/ui/molecules/MyJams/JamChallengeDetails/JamTask/TaskStatus';
import { useActiveRouteDetails } from '@/src/hooks/useActiveRouteDetails';
import { RoutePath } from '@/src/RoutePath';
import {
  JAM_CHALLENGE_DETAILS_ROUTES,
  JAM_CHALLENGE_ROUTES,
  JAM_CHALLENGE_TASK_ROUTES,
  JAM_EVENT_DETAILS_ROUTES,
  JAM_FACILITATOR_DETAILS_ROUTES,
} from '@/src/routes';
import { useEditCampaign } from '@/src/store/edit-campaign.context';
import { useEditEvent } from '@/src/store/edit-event.context';
import { useJamChallengeDetails } from '@/src/store/jam-challenge-details.context';
import { useJamChallenge } from '@/src/store/jam-challenge.context';
import { useJamChat } from '@/src/store/jam-chat.context';
import { useJamEventDetails } from '@/src/store/jam-event-details.context';
import { useSplitPanel } from '@/src/store/split-panel.context';
import { useToolPanel } from '@/src/store/tool-panel.context';
import { JamEventStatusForSidebar, JamTaskProgress } from '@/src/types/JamChallengeDetails';
import { config } from '@/src/config/app-config';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { SideNavigation } from '@amzn/awsui-components-react';
import { SideNavigationProps } from '@amzn/awsui-components-react/polaris/side-navigation/interfaces';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import styles from './MyJamSidebar.module.scss';
import { isAITeammatePathname } from '@/src/utils/url.utils';
import { omit } from 'lodash';

/**
 * Types that allow attaching custom "onClick" behavior to side navigation items.
 */
type FollowDetailWithClick = SideNavigationProps.FollowDetail & { onClick?: () => void };

// Stable keys for remembering expand/collapse choices across renders.
const CHALLENGES_GROUP_KEY = 'challenges';
const FACILITATOR_GROUP_KEY = 'facilitator';
const challengeGroupKey = (challengeId: string) => `challenge:${challengeId}`;

export const MyJamSidebar: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { activeRoute } = useActiveRouteDetails();
  const [activeNav, setActiveNav] = useState('');
  const { toggleShowSplitPanel } = useSplitPanel();
  const { toggleShowToolPanel, aiTeammateChatIsVisible } = useToolPanel();
  const { newEventMode } = useEditEvent();
  const { newCampaignMode } = useEditCampaign();
  const { jamChallengeData } = useJamChallenge();
  const {
    selectedJamChallenge,
    jamChallengeId,
    challengeProgress,
    progressById,
    challengeStatuses,
    outputProperties,
    challengeSupportDetails,
  } = useJamChallengeDetails();
  const { event, eventName, unreadMessageCounts } = useJamEventDetails();
  const { allSupportChats } = useJamChat();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const [jamTasks, setJamTasks] = useState<{ [key: string]: SideNavigationProps.Item[] }>({});

  // Cloudscape resets a group's expansion to `defaultExpanded` whenever its item object changes,
  // which is every render here. Keep the user's choices and re-derive `defaultExpanded` from them.
  const [expandedOverrides, setExpandedOverrides] = useState<{ [groupKey: string]: boolean }>({});
  const groupKeys = new WeakMap<object, string>();
  const trackGroup = <T extends object>(groupKey: string, item: T): T => {
    groupKeys.set(item, groupKey);
    return item;
  };
  const isGroupExpanded = (groupKey: string, fallback: boolean) => expandedOverrides[groupKey] ?? fallback;
  const onGroupChange: SideNavigationProps['onChange'] = (e) => {
    const groupKey = groupKeys.get(e.detail.item);
    if (groupKey) {
      setExpandedOverrides((prev) => ({ ...prev, [groupKey]: e.detail.expanded }));
    }
  };

  // Navigating to a challenge always opens it, even if the user collapsed it earlier.
  useEffect(() => {
    if (!jamChallengeId) {
      return;
    }
    const groupKey = challengeGroupKey(jamChallengeId);
    setExpandedOverrides((prev) => (groupKey in prev ? omit(prev, groupKey) : prev));
  }, [jamChallengeId]);

  const isAnyChallengeUnlocked = React.useMemo(() => {
    return jamChallengeData?.challenges?.some(({ locked }) => !locked);
  }, [jamChallengeData?.challenges]);

  // If the provided link is empty, do not redirect pages
  const onFollow = (e: CustomEvent<FollowDetailWithClick>) => {
    toggleShowSplitPanel(false);
    if (!aiTeammateChatIsVisible || isAITeammatePathname(e.detail.href)) {
      toggleShowToolPanel(false);
    }
    e.preventDefault();

    if (e.detail.onClick) {
      e.detail.onClick();
    } else if (e.detail.href) {
      history.push(e.detail.href);
    }
  };

  useEffect(() => {
    if (location.hash) {
      if (newEventMode) setActiveNav(location.hash);
      if (newCampaignMode) setActiveNav(location.hash);
    } else {
      setActiveNav(activeRoute);
    }
  }, [activeRoute, location.pathname]);

  // Update the challenge progress/information for the whole event.
  // This usually happens when the page loads or a websocket message comes in updating the challenge list.
  useEffect(() => {
    jamChallengeData?.challenges?.forEach((challenge) => {
      // Skip the selected challenge: its task nav is owned by the live overlay below, which is
      // more current than list data.
      if (challenge.id === challengeProgress?.challengeId) {
        return;
      }
      // Prefer the status hook's tracked progress (kept current by websockets + backfill) over
      // list-data taskProgress, which is a snapshot that can go stale. Fall back to list data when
      // no tracked progress exists yet.
      const fetched = progressById[challenge.id];
      const taskProgress = fetched?.taskProgress?.length ? fetched.taskProgress : challenge.taskProgress;
      const started = fetched?.taskProgress?.length ? !!fetched.started : !!challenge.started;
      if (!taskProgress?.length) {
        return;
      }
      setJamTasks((prevState) => ({
        ...prevState,
        [challenge.id]: getJamTaskNavigationFromTaskProgress(challenge.id, challenge.locked, started, taskProgress),
      }));
    });
  }, [jamChallengeData, progressById, challengeProgress?.challengeId]);

  // Update the sidebar using progress data from the current challenge being worked on.
  // Since it's updated from the learner's own local actions, it's going to be more
  // up to date than the data in `jamChallengeData`.
  useEffect(() => {
    if (challengeProgress) {
      setJamTasks((prevState) => {
        return {
          ...prevState,
          // `challengeProgress` lacks information about whether the challenge is locked.
          // Since the challenge is currently being worked on, it can be assumed to be unlocked.
          // If a facilitator later locks the challenge, jamChallengeData will be updated via websocket, and the previous hook will run to lock the challenges as appropriate.
          [challengeProgress.challengeId]: getJamTaskNavigationFromTaskProgress(
            challengeProgress.challengeId,
            false,
            challengeProgress.started,
            challengeProgress.taskProgress
          ),
        };
      });
    }
  }, [challengeProgress]);

  const validateTaskLink = (
    challengeId: string,
    challengeLocked: boolean,
    challengeStarted: boolean,
    task: JamTaskProgress
  ) => {
    if (
      task.locked ||
      // let facilitator navigate without challenge start
      ((challengeLocked || !challengeStarted) && !event?.facilitator)
    ) {
      return '';
    }
    return JAM_CHALLENGE_TASK_ROUTES.resolve([eventName, challengeId, task.taskId]);
  };

  const getJamTaskNavigationFromTaskProgress = (
    challengeId: string,
    challengeLocked: boolean,
    challengeStarted: boolean,
    taskProgress?: JamTaskProgress[]
  ): SideNavigationProps.Item[] => {
    if (!taskProgress?.length) return [];
    const sorted = [...taskProgress];
    sorted.sort((a, b) => a.taskNumber - b.taskNumber);
    return sorted.map((task) => ({
      type: 'link',
      text: `${t(i18nKeys.myJams.challenges.details.task.noTitle)} ${task?.taskNumber}`,
      href: validateTaskLink(challengeId, challengeLocked, challengeStarted, task),
      info: (
        <TaskStatus
          taskCompleted={task.completed}
          taskLocked={task.locked}
          status={event?.eventStatus}
          challengeStatus={challengeStarted}
        />
      ),
    }));
  };

  const getJamChallengesDetailRoutes = (challengeId: string): SideNavigationProps.Item[] => {
    return [
      {
        type: 'link',
        text: t(i18nKeys.myJams.challenges.details.overview.title),
        href: JAM_CHALLENGE_DETAILS_ROUTES.Overview.resolve([eventName, challengeId]),
      },
      // Only show output properties if the challenge is currently selected.
      // This is a limitation of the `outputProperties` context object, which only holds output properties for
      // the currently selected challenge.
      // TODO - Improve the context implementation to contain output property information for all challenges.
      //    https://sim.amazon.com/issues/JAM-13694
      ...(challengeId === jamChallengeId &&
      !selectedJamChallenge?.locked &&
      outputProperties &&
      outputProperties.length > 0
        ? ([
            {
              type: 'link',
              text: t(i18nKeys.myJams.challenges.details.outputProperties.title),
              href: JAM_CHALLENGE_DETAILS_ROUTES.OutputProperties.resolve([eventName, challengeId]),
            },
          ] as SideNavigationProps.Item[])
        : []),
      // Arena (SCENARIO) challenges are judged on the overview page; their placeholder task
      // is not navigable. Same selected-challenge limitation as Output Properties above.
      ...(config.ff.enableArena && challengeId === jamChallengeId && selectedJamChallenge?.arena
        ? []
        : jamTasks[challengeId] || []),
      ...(event?.facilitator
        ? ([
            {
              type: 'link',
              text: t(i18nKeys.myJams.challenges.details.sidebar.recentFeedback),
              href: JAM_CHALLENGE_DETAILS_ROUTES.RecentFeedback.resolve([eventName, challengeId]),
            },
          ] as SideNavigationProps.Item[])
        : []),
      ...(event?.facilitator && event.testEvent
        ? ([
            {
              type: 'link',
              text: t(i18nKeys.myJams.challenges.details.sidebar.issues),
              href: JAM_CHALLENGE_DETAILS_ROUTES.Issue.resolve([eventName, challengeId]),
            },
          ] as SideNavigationProps.Item[])
        : []),
      ...(event?.facilitator && challengeSupportDetails?.getChallengeSupportDetails(challengeId)?.wiki?.length
        ? ([
            {
              type: 'link',
              text: t(i18nKeys.myJams.challenges.details.sidebar.wiki),
              href: JAM_CHALLENGE_DETAILS_ROUTES.Wiki.resolve([eventName, challengeId]),
            },
          ] as SideNavigationProps.Item[])
        : []),
      ...(event?.facilitator
        ? ([
            {
              type: 'link',
              text: t(i18nKeys.myJams.challenges.details.sidebar.faciliatorNotes),
              href: JAM_CHALLENGE_DETAILS_ROUTES.FacilitatorNotes.resolve([eventName, challengeId]),
            },
          ] as SideNavigationProps.Item[])
        : []),
    ];
  };

  const AllJamChallenges: SideNavigationProps.Item[] =
    jamChallengeData?.challenges?.map((challenge) => {
      const lockedNode = challenge.locked ? <TaskStatus name={JamEventStatusForSidebar.LOCKED} /> : undefined;
      const label = challengeSidebarNavText({
        title: challenge.title ?? challenge.id,
        status: challenge.locked ? undefined : challengeStatuses[challenge.id],
        statusNode: lockedNode,
      });
      const groupKey = challengeGroupKey(challenge.id);
      return trackGroup(groupKey, {
        type: 'expandable-link-group',
        text: label,
        defaultExpanded: isGroupExpanded(groupKey, jamChallengeId === challenge.id),
        href: !challenge.locked ? JAM_CHALLENGE_ROUTES.resolve([eventName, challenge.id]) : '',
        items: getJamChallengesDetailRoutes(challenge.id),
      } as SideNavigationProps.ExpandableLinkGroup);
    }) ?? [];

  // it is possible that below items will be filtered based on
  // isSupportUser access. right now if user is even support user
  // he will be able to see the facilitator menu
  const FacilitatorMenu: SideNavigationProps.Section = trackGroup(FACILITATOR_GROUP_KEY, {
    type: 'section',
    text: t(i18nKeys.facilitator.title),
    defaultExpanded: isGroupExpanded(FACILITATOR_GROUP_KEY, true),
    items: [
      {
        type: 'link',
        text: t(i18nKeys.facilitator.participants.title),
        href: JAM_FACILITATOR_DETAILS_ROUTES.Participants.resolve(eventName),
      },
      {
        type: 'link',
        text: t(i18nKeys.facilitator.jamSettings.title),
        href: JAM_FACILITATOR_DETAILS_ROUTES.JamSettings.resolve(eventName),
      },
      {
        type: 'link',
        text: t(i18nKeys.facilitator.messaging.title),
        href: JAM_FACILITATOR_DETAILS_ROUTES.Messaging.resolve(eventName),
      },
      ...(event?.supportChatEnabled
        ? [
            {
              type: 'link',
              text:
                t(i18nKeys.facilitator.supportChats.title) +
                (allSupportChats.length > 0 ? ` (${allSupportChats.length})` : ''),
              href: JAM_FACILITATOR_DETAILS_ROUTES.SupportChats.resolve(eventName),
            } as SideNavigationProps.Item,
          ]
        : []),
      {
        type: 'link',
        text: t(i18nKeys.facilitator.notifications.notification),
        href: JAM_FACILITATOR_DETAILS_ROUTES.Notifications.resolve(eventName),
      },
      ...(config.ff.enableFacilitatorTools
        ? [
            {
              type: 'link',
              text: t(i18nKeys.facilitator.actionLog.title),
              href: JAM_FACILITATOR_DETAILS_ROUTES.AuditLog.resolve(eventName),
            } as SideNavigationProps.Item,
          ]
        : []),
    ],
  });

  return (
    <div className={styles.root} data-testid="jam-sidebar">
      <SideNavigation
        activeHref={activeNav}
        header={{
          href: RoutePath.MY_JAMS,
          text: event ? (event.title.length > 40 ? event.title.slice(0, 40) + '...' : event.title) : 'AWS Jam',
        }}
        items={[
          ...(event?.gamified || event?.facilitator
            ? [
                {
                  type: 'link',
                  text: t(i18nKeys.jamDashboard.leaderboard.title),
                  href: JAM_EVENT_DETAILS_ROUTES.LeaderBoard.resolve(eventName),
                } as SideNavigationProps.Item,
              ]
            : []),
          trackGroup(CHALLENGES_GROUP_KEY, {
            type: isAnyChallengeUnlocked ? 'expandable-link-group' : 'link-group',
            text: t(i18nKeys.challenges.title),
            items: isAnyChallengeUnlocked ? AllJamChallenges : [],
            href: JAM_EVENT_DETAILS_ROUTES.Challenges.resolve(eventName),
            defaultExpanded: isGroupExpanded(CHALLENGES_GROUP_KEY, true),
          } as SideNavigationProps.Item),
          ...(!event?.isSingleParticipantEvent && !event?.isSplEvent
            ? [
                {
                  type: 'link',
                  text: t(i18nKeys.myJams.team.title),
                  href: JAM_EVENT_DETAILS_ROUTES.Team.resolve(eventName),
                } as SideNavigationProps.Item,
              ]
            : []),
          ...(!event?.facilitator
            ? [
                {
                  type: 'link',
                  text: t(i18nKeys.myJams.feedback.title),
                  href: JAM_EVENT_DETAILS_ROUTES.Feedback.resolve(eventName),
                } as SideNavigationProps.Item,
              ]
            : []),
          {
            type: 'link',
            text: `${t(i18nKeys.myJams.messages.title)} ${unreadMessageCounts ? `(${unreadMessageCounts})` : ''}`,
            href: JAM_EVENT_DETAILS_ROUTES.Messages.resolve(eventName),
          },
          ...(event?.supportUser || event?.facilitator ? [FacilitatorMenu] : []),
        ]}
        onFollow={onFollow}
        onChange={onGroupChange}
      />
      <div className={styles.spacer} />
    </div>
  );
};
